import { IUser, IUserRole, UserProfile, UserService } from '@person/user/data-access';
import { patchState, signalStore, withMethods, withState, withComputed } from '@ngrx/signals';
import { inject, computed, Injector } from '@angular/core';
import { AccountService, AuthService, AuthStore, IAuthAccessToken, IChangePaswordRequest } from '@account/data-access';
import { Encrypt, getCyclicColor } from '@shared/utils';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { firstValueFrom, pipe, switchMap } from 'rxjs';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AlertStore } from '@alert/data-access';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { tapResponse } from '@ngrx/operators';
import { IPersonDataAsync } from './entities/personDataAsync.interface';
import { PersonService } from '@person/register/data-access';
import { adminTenants } from '@application/config';
import { TemporaryAuthModalConfig, TemporaryAuthModalService } from '@shared/components/ui/temporary-auth-modal';
import { SystemModulesStore } from './system-modules.store';
import { ErrorStore } from '@application/errors/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries -- userSettings/uiClientSettings types shared with shortcuts; consider moving to person/user or shared types lib to break cycle
import { IUserSettings } from '@shared/components/shortcuts/data-access';
import {
  IUiClientSettings,
  parseUiClientSettings,
  mergeUiClientSettings,
  DefaultUiClientSettings,
  storageFormatToSearchOptions,
  storageFormatToPersonActions,
} from '@shared/components/shortcuts/data-access';

export interface ISelfUserStore {
  personDataAsync: IPersonDataAsync;
  userData: IUser;
  userRole: IUserRole;
  allPersonsByUser: IUserRole[],
  userProfile: UserProfile;
  impersonatedUserProfile: UserProfile;
  temporaryPermissionsExpiresAt: number | null;
  userSettings: IUserSettings | null;
  uiClientSettings: IUiClientSettings;
  /** True when uiClientSettings were modified locally and not yet persisted */
  uiClientSettingsDirty: boolean;
}

/** Emits when uiClientSettings should be persisted. Subscribed by ShortcutsStore. */
export const uiClientSettingsPersistenceSubject = new Subject<void>();

export const initialSelfUserStore: ISelfUserStore = {
  personDataAsync: null,
  userData: null,
  userRole: null,
  allPersonsByUser: null,
  userProfile: null,
  impersonatedUserProfile: null,
  temporaryPermissionsExpiresAt: null,
  userSettings: null,
  uiClientSettings: DefaultUiClientSettings,
  uiClientSettingsDirty: false,
};


const TEMPORARY_AUTH_TRANSLATION_PATH = 'TEMPORARY_AUTH_MODAL';

/** Grace period (ms) after a local ElasticSearch change during which server settings are not applied. */
export const ELASTIC_SEARCH_LOCAL_CHANGE_GRACE_PERIOD_MS = 2000;

export enum PermissionNumber {
  AcaoPorPessoaDisparar = 1,
  AcessosObterTodos = 2,
  AcoesAdicionar = 3,
  AcoesAdicionarAcoesPessoas = 4,
  AcoesAdicionarCometarios = 5,
  AcoesAtualizar = 6,
  AcoesAtualizarEtapas = 7,
  AcoesDetalhamentosObterTodos = 8,
  AcoesObterDetalhamentoPorId = 9,
  AcoesObterPorId = 10,
  AcoesObterTodos = 11,
  AcoesRemoverPorPessoaId = 12,
  AdicionarAssinaturaPessoaTransferencia = 13,
  AjusteAtualizar = 14,
  AjusteEstoqueProdutoAdicionar = 15,
  AjusteEstoqueProdutoAtualizar = 16,
  AjusteEstoqueProdutoObterPorId = 17,
  AjusteEstoqueProdutoObterTodos = 18,
  AjusteEstoqueProdutoRemover = 19,
  AssinaturaAdicionar = 20,
  AssinaturaAgrupadoraAdicionar = 21,
  AssinaturaAgrupadoraAtualizar = 22,
  AssinaturaAgrupadoraObterPorId = 23,
  AssinaturaAgrupadoraRemover = 24,
  AssinaturaAtualizar = 25,
  AssinaturaHorarioAdicionar = 26,
  AssinaturaHorarioAtualizar = 27,
  AssinaturaHorarioObterPorId = 28,
  AssinaturaHorarioObterTodos = 29,
  AssinaturaHorarioRemover = 30,
  AssinaturaObterPorId = 31,
  AssinaturaObterTodos = 32,
  AssinaturaOcupacaoObterTodos = 33,
  AssinaturaPessoaAdicionar = 34,
  AssinaturaPessoaAdicionarRecesso = 35,
  AssinaturaPessoaAdicionarRecessoColetivo = 36,
  AssinaturaPessoaAdicionarTrancamento = 37,
  AssinaturaPessoaAlterar = 38,
  AssinaturaPessoaAtualizar = 39,
  AssinaturaPessoaEditarRecesso = 40,
  AssinaturaPessoaEditarTrancamento = 41,
  AssinaturaPessoaEncerramentoObterTodos = 42,
  AssinaturaPessoaFrequenciaObterTodos = 43,
  AssinaturaPessoaObterPorId = 44,
  AssinaturaPessoaObterPorIdTrancamento = 45,
  AssinaturaPessoaObterTodos = 46,
  AssinaturaPessoaObterTodosRecessos = 47,
  AssinaturaPessoaRemover = 48,
  AssinaturaPessoaRemoverTrancamento = 49,
  AssinaturaPessoaRenovar = 50,
  AssinaturaPessoaTrancamentoObterTodos = 51,
  AssinaturaRemover = 52,
  AtualizarCheque = 53,
  AutomacoesAdicionar = 54,
  AutomacoesAtualizar = 55,
  AutomacoesObterPorId = 56,
  AutomacoesObterTodos = 57,
  AutomacoesRemover = 58,
  AvaliacaoAdicionarAnamnese = 59,
  AvaliacaoAdicionarCapacidadeAerobicaVO2 = 60,
  AvaliacaoAdicionarComposicaoCorporalPerimetria = 61,
  AvaliacaoAdicionarFlexibilidade = 62,
  AvaliacaoAdicionarForcaResistenciaMuscular = 63,
  AvaliacaoAtualizarAnamnese = 64,
  AvaliacaoAtualizarComposicaoCorporalPerimetria = 65,
  AvaliacaoAtualizarFlexibilidade = 66,
  AvaliacaoAtualizarForcaResistenciaMuscular = 67,
  AvaliacaoObterPorId = 68,
  AvaliacaoObterTodos = 69,
  AvaliacaoRemover = 70,
  CaixaAbrir = 71,
  CaixaFechar = 72,
  CaixaLancamentoRetroativo = 73,
  CaixaObterTodos = 74,
  CancelarVendaAssinatura = 75,
  CancelarVendaAssinaturaAgrupadora = 76,
  CartoesRecebidos = 77,
  CategoriaAdicionar = 78,
  CategoriaAtualizar = 79,
  CategoriaObterPorId = 80,
  CategoriaRemover = 81,
  CategoriasObterTodos = 82,
  CedenteObterTodos = 83,
  ChequesRecebidos = 84,
  ClienteAdicionar = 85,
  ClienteAtualizar = 86,
  ClienteObterPorId = 87,
  ClienteObterTodos = 88,
  ClienteRemover = 89,
  ComissaoConsultores = 90,
  ComissaoProfessores = 91,
  CompraAdicionar = 92,
  CompraAtualizar = 93,
  CompraObterPorId = 94,
  CompraObterTodos = 95,
  CompraRemover = 96,
  CondicaoVendaProdutoAdicionar = 97,
  CondicaoVendaProdutoAtualizar = 98,
  CondicaoVendaProdutoObterPorId = 99,
  CondicaoVendaProdutoObterTodos = 100,
  CondicaoVendaProdutoRemover = 101,
  ContaBancariaAdicionar = 102,
  ContaBancariaAtualizar = 103,
  ContaBancariaObterPorId = 104,
  ContaBancariaObterTodos = 105,
  ContaBancariaRemover = 106,
  ContaPagarAdicionar = 107,
  ContaPagarAtualizar = 108,
  ContaPagarObterPorId = 109,
  ContaPagarObterTodos = 110,
  ContaPagarRemover = 111,
  ContaReceberAdicionar = 112,
  ContaReceberAtualizar = 113,
  ContaReceberObterPorId = 114,
  ContaReceberObterTodos = 115,
  ContaReceberReceber = 116,
  ContaReceberRemover = 117,
  ContatoAdicionar = 118,
  ContatoAtualizar = 119,
  ContatoObterPorId = 120,
  ContatoObterTodos = 121,
  ContatoRemover = 122,
  ContratoAdicionar = 123,
  ContratoAtualizar = 124,
  ContratoObterPorId = 125,
  ContratoObterTodos = 126,
  ContratoRemover = 127,
  ConvenioCartaoAdicionar = 128,
  ConvenioCartaoAtualizar = 129,
  ConvenioCartaoObterPorId = 130,
  ConvenioCartaoObterTodos = 131,
  ConvenioCartaoRemover = 132,
  CreditosClientesObterTodos = 133,
  DescontoAdicionar = 134,
  DescontoAtualizar = 135,
  DescontoObterPorId = 136,
  DescontoObterTodos = 137,
  DescontoRemover = 138,
  DocumentoAdicionar = 139,
  DocumentoAtualizar = 140,
  DocumentoObterPorId = 141,
  DocumentoObterTodos = 142,
  DocumentoRemover = 143,
  EncerrarAssinatura = 144,
  ExercicioAdicionar = 145,
  ExercicioAtualizar = 146,
  ExercicioObterPorId = 147,
  ExercicioObterTodos = 148,
  ExercicioRemover = 149,
  FaturaAtualizar = 150,
  FaturamentoPorPessoaId = 151,
  FaturaObterTodos = 152,
  FaturaRemover = 153,
  FichaAdicionar = 154,
  FichaAtualizar = 155,
  FichaObterPorId = 156,
  FichaObterTodos = 157,
  FichaRemover = 158,
  FornecedorAdicionar = 159,
  FornecedorAtualizar = 160,
  FornecedorObterPorId = 161,
  FornecedorObterTodos = 162,
  FornecedorRemover = 163,
  FuncoesAdicionar = 164,
  FuncoesAtualizar = 165,
  FuncoesObterPorId = 166,
  FuncoesObterTodos = 167,
  FuncoesRemover = 168,
  FunisAdicionar = 169,
  FunisAdicionarGatilho = 170,
  FunisAtualizar = 171,
  FunisObterPorId = 172,
  FunisObterTodos = 173,
  FunisRemover = 174,
  GetChatInfo = 175,
  LiberacaoAssinaturaPessoaAdicionar = 176,
  LiberacaoAssinaturaPessoaRemover = 177,
  ListarTodasApuracoes = 178,
  ListChats = 179,
  MultiploAgendamento = 180,
  NovoEncerramentoAssinaturaPessoa = 181,
  ObterAssinaturaPessoaEncerramentoPorId = 182,
  ObterAssinaturaPessoaRecessoPorId = 183,
  ObterChequePorId = 184,
  ObterPedidoVenda = 185,
  PagamentoAdicionar = 186,
  PagamentoAtualizar = 187,
  PagamentoEstornar = 188,
  PagamentoObterPorId = 189,
  PagamentoObterTodos = 190,
  PagamentoRemover = 191,
  PostagemAdicionar = 192,
  PostagemAtualizar = 193,
  PostagemObterPorId = 194,
  PostagemObterTodos = 195,
  PostagemRemover = 196,
  ProdutoAdicionar = 197,
  ProdutoAtualizar = 198,
  ProdutoObterPorId = 199,
  ProdutoObterTodos = 200,
  ProdutoRemover = 201,
  RecebimentoAtualizarJuros = 202,
  RecebimentoAtualizarMulta = 203,
  RecebimentoDescontoCadastrado = 204,
  RecebimentoDescontoManual = 205,
  RecebimentoEstornar = 206,
  RecebimentoObterTodos = 207,
  RemessaObterTodos = 208,
  RemoverAssinaturaPessoaRecesso = 209,
  RetornoAdicionar = 210,
  RetornoObterTodos = 211,
  ServicoAdicionar = 212,
  ServicoAtualizar = 213,
  ServicoObterPorId = 214,
  ServicoObterTodos = 215,
  ServicoPessoaAdicionar = 216,
  ServicoPessoaAtualizar = 217,
  ServicoPessoaObterPorId = 218,
  ServicoPessoaObterTodos = 219,
  ServicoPessoaRemover = 220,
  ServicoRemover = 221,
  TipoContaPagarAdicionar = 222,
  TipoContaPagarAtualizar = 223,
  TipoContaPagarObterPorId = 224,
  TipoContaPagarRemover = 225,
  TiposContaPagarObterTodos = 226,
  TreinoPreDefinidoAdicionar = 227,
  TreinoPreDefinidoAtualizar = 228,
  TreinoPreDefinidoObterPorId = 229,
  TreinoPreDefinidoObterTodos = 230,
  TreinoPreDefinidoRemover = 231,
  UsuarioAdicionar = 232,
  UsuarioAtualizar = 233,
  UsuarioObterPorId = 234,
  UsuarioObterTodos = 235,
  UsuarioRemover = 236,
  VendaAdicionar = 237,
  VendaAdicionarDesconto = 238,
  VendaAtualizar = 239,
  VendaObterPedido = 240,
  VendaObterPorId = 241,
  VendaObterTodos = 242,
  VendaRemover = 243,
  VisaoGeralAdministrador = 244,
  VisaoGeralImplatacao = 245,
  VisaoGeralSuporte = 246,
  VisaoGeralVendas = 247,
  VisitanteAdicionar = 248,
  VisitanteAtualizar = 249,
  VisitanteObterPorId = 250,
  VisitanteObterTodos = 251,
  VisitanteRemover = 252,
}

export enum PermissionText {
  AcaoPorPessoaDisparar = "AcaoPorPessoaDisparar",
  AcessosObterTodos = "AcessosObterTodos",
  AcoesAdicionar = "AcoesAdicionar",
  AcoesAdicionarAcoesPessoas = "AcoesAdicionarAcoesPessoas",
  AcoesAdicionarCometarios = "AcoesAdicionarCometarios",
  AcoesAtualizar = "AcoesAtualizar",
  AcoesAtualizarEtapas = "AcoesAtualizarEtapas",
  AcoesDetalhamentosObterTodos = "AcoesDetalhamentosObterTodos",
  AcoesObterDetalhamentoPorId = "AcoesObterDetalhamentoPorId",
  AcoesObterPorId = "AcoesObterPorId",
  AcoesObterTodos = "AcoesObterTodos",
  AcoesRemoverPorPessoaId = "AcoesRemoverPorPessoaId",
  AdicionarAssinaturaPessoaTransferencia = "AdicionarAssinaturaPessoaTransferencia",
  AjusteAtualizar = "AjusteAtualizar",
  AjusteEstoqueProdutoAdicionar = "AjusteEstoqueProdutoAdicionar",
  AjusteEstoqueProdutoAtualizar = "AjusteEstoqueProdutoAtualizar",
  AjusteEstoqueProdutoObterPorId = "AjusteEstoqueProdutoObterPorId",
  AjusteEstoqueProdutoObterTodos = "AjusteEstoqueProdutoObterTodos",
  AjusteEstoqueProdutoRemover = "AjusteEstoqueProdutoRemover",
  AssinaturaAdicionar = "AssinaturaAdicionar",
  AssinaturaAgrupadoraAdicionar = "AssinaturaAgrupadoraAdicionar",
  AssinaturaAgrupadoraAtualizar = "AssinaturaAgrupadoraAtualizar",
  AssinaturaAgrupadoraObterPorId = "AssinaturaAgrupadoraObterPorId",
  AssinaturaAgrupadoraRemover = "AssinaturaAgrupadoraRemover",
  AssinaturaAtualizar = "AssinaturaAtualizar",
  AssinaturaHorarioAdicionar = "AssinaturaHorarioAdicionar",
  AssinaturaHorarioAtualizar = "AssinaturaHorarioAtualizar",
  AssinaturaHorarioObterPorId = "AssinaturaHorarioObterPorId",
  AssinaturaHorarioObterTodos = "AssinaturaHorarioObterTodos",
  AssinaturaHorarioRemover = "AssinaturaHorarioRemover",
  AssinaturaObterPorId = "AssinaturaObterPorId",
  AssinaturaObterTodos = "AssinaturaObterTodos",
  AssinaturaOcupacaoObterTodos = "AssinaturaOcupacaoObterTodos",
  AssinaturaPessoaAdicionar = "AssinaturaPessoaAdicionar",
  AssinaturaPessoaAdicionarRecesso = "AssinaturaPessoaAdicionarRecesso",
  AssinaturaPessoaAdicionarRecessoColetivo = "AssinaturaPessoaAdicionarRecessoColetivo",
  AssinaturaPessoaAdicionarTrancamento = "AssinaturaPessoaAdicionarTrancamento",
  AssinaturaPessoaAlterar = "AssinaturaPessoaAlterar",
  AssinaturaPessoaAtualizar = "AssinaturaPessoaAtualizar",
  AssinaturaPessoaEditarRecesso = "AssinaturaPessoaEditarRecesso",
  AssinaturaPessoaEditarTrancamento = "AssinaturaPessoaEditarTrancamento",
  AssinaturaPessoaEncerramentoObterTodos = "AssinaturaPessoaEncerramentoObterTodos",
  AssinaturaPessoaFrequenciaObterTodos = "AssinaturaPessoaFrequenciaObterTodos",
  AssinaturaPessoaObterPorId = "AssinaturaPessoaObterPorId",
  AssinaturaPessoaObterPorIdTrancamento = "AssinaturaPessoaObterPorIdTrancamento",
  AssinaturaPessoaObterTodos = "AssinaturaPessoaObterTodos",
  AssinaturaPessoaObterTodosRecessos = "AssinaturaPessoaObterTodosRecessos",
  AssinaturaPessoaRemover = "AssinaturaPessoaRemover",
  AssinaturaPessoaRemoverTrancamento = "AssinaturaPessoaRemoverTrancamento",
  AssinaturaPessoaRenovar = "AssinaturaPessoaRenovar",
  AssinaturaPessoaTrancamentoObterTodos = "AssinaturaPessoaTrancamentoObterTodos",
  AssinaturaRemover = "AssinaturaRemover",
  AtualizarCheque = "AtualizarCheque",
  AutomacoesAdicionar = "AutomacoesAdicionar",
  AutomacoesAtualizar = "AutomacoesAtualizar",
  AutomacoesObterPorId = "AutomacoesObterPorId",
  AutomacoesObterTodos = "AutomacoesObterTodos",
  AutomacoesRemover = "AutomacoesRemover",
  AvaliacaoAdicionarAnamnese = "AvaliacaoAdicionarAnamnese",
  AvaliacaoAdicionarCapacidadeAerobicaVO2 = "AvaliacaoAdicionarCapacidadeAerobicaVO2",
  AvaliacaoAdicionarComposicaoCorporalPerimetria = "AvaliacaoAdicionarComposicaoCorporalPerimetria",
  AvaliacaoAdicionarFlexibilidade = "AvaliacaoAdicionarFlexibilidade",
  AvaliacaoAdicionarForcaResistenciaMuscular = "AvaliacaoAdicionarForcaResistenciaMuscular",
  AvaliacaoAtualizarAnamnese = "AvaliacaoAtualizarAnamnese",
  AvaliacaoAtualizarComposicaoCorporalPerimetria = "AvaliacaoAtualizarComposicaoCorporalPerimetria",
  AvaliacaoAtualizarFlexibilidade = "AvaliacaoAtualizarFlexibilidade",
  AvaliacaoAtualizarForcaResistenciaMuscular = "AvaliacaoAtualizarForcaResistenciaMuscular",
  AvaliacaoObterPorId = "AvaliacaoObterPorId",
  AvaliacaoObterTodos = "AvaliacaoObterTodos",
  AvaliacaoRemover = "AvaliacaoRemover",
  CaixaAbrir = "CaixaAbrir",
  CaixaFechar = "CaixaFechar",
  CaixaLancamentoRetroativo = "CaixaLancamentoRetroativo",
  CaixaObterTodos = "CaixaObterTodos",
  CancelarVendaAssinatura = "CancelarVendaAssinatura",
  CancelarVendaAssinaturaAgrupadora = "CancelarVendaAssinaturaAgrupadora",
  CartoesRecebidos = "CartoesRecebidos",
  CategoriaAdicionar = "CategoriaAdicionar",
  CategoriaAtualizar = "CategoriaAtualizar",
  CategoriaObterPorId = "CategoriaObterPorId",
  CategoriaRemover = "CategoriaRemover",
  CategoriasObterTodos = "CategoriasObterTodos",
  CedenteObterTodos = "CedenteObterTodos",
  ChequesRecebidos = "ChequesRecebidos",
  ClienteAdicionar = "ClienteAdicionar",
  ClienteAtualizar = "ClienteAtualizar",
  ClienteObterPorId = "ClienteObterPorId",
  ClienteObterTodos = "ClienteObterTodos",
  ClienteRemover = "ClienteRemover",
  ComissaoConsultores = "ComissaoConsultores",
  ComissaoProfessores = "ComissaoProfessores",
  CompraAdicionar = "CompraAdicionar",
  CompraAtualizar = "CompraAtualizar",
  CompraObterPorId = "CompraObterPorId",
  CompraObterTodos = "CompraObterTodos",
  CompraRemover = "CompraRemover",
  CondicaoVendaProdutoAdicionar = "CondicaoVendaProdutoAdicionar",
  CondicaoVendaProdutoAtualizar = "CondicaoVendaProdutoAtualizar",
  CondicaoVendaProdutoObterPorId = "CondicaoVendaProdutoObterPorId",
  CondicaoVendaProdutoObterTodos = "CondicaoVendaProdutoObterTodos",
  CondicaoVendaProdutoRemover = "CondicaoVendaProdutoRemover",
  ContaBancariaAdicionar = "ContaBancariaAdicionar",
  ContaBancariaAtualizar = "ContaBancariaAtualizar",
  ContaBancariaObterPorId = "ContaBancariaObterPorId",
  ContaBancariaObterTodos = "ContaBancariaObterTodos",
  ContaBancariaRemover = "ContaBancariaRemover",
  ContaPagarAdicionar = "ContaPagarAdicionar",
  ContaPagarAtualizar = "ContaPagarAtualizar",
  ContaPagarObterPorId = "ContaPagarObterPorId",
  ContaPagarObterTodos = "ContaPagarObterTodos",
  ContaPagarRemover = "ContaPagarRemover",
  ContaReceberAdicionar = "ContaReceberAdicionar",
  ContaReceberAtualizar = "ContaReceberAtualizar",
  ContaReceberObterPorId = "ContaReceberObterPorId",
  ContaReceberObterTodos = "ContaReceberObterTodos",
  ContaReceberReceber = "ContaReceberReceber",
  ContaReceberRemover = "ContaReceberRemover",
  ContatoAdicionar = "ContatoAdicionar",
  ContatoAtualizar = "ContatoAtualizar",
  ContatoObterPorId = "ContatoObterPorId",
  ContatoObterTodos = "ContatoObterTodos",
  ContatoRemover = "ContatoRemover",
  ContratoAdicionar = "ContratoAdicionar",
  ContratoAtualizar = "ContratoAtualizar",
  ContratoObterPorId = "ContratoObterPorId",
  ContratoObterTodos = "ContratoObterTodos",
  ContratoRemover = "ContratoRemover",
  ConvenioCartaoAdicionar = "ConvenioCartaoAdicionar",
  ConvenioCartaoAtualizar = "ConvenioCartaoAtualizar",
  ConvenioCartaoObterPorId = "ConvenioCartaoObterPorId",
  ConvenioCartaoObterTodos = "ConvenioCartaoObterTodos",
  ConvenioCartaoRemover = "ConvenioCartaoRemover",
  CreditosClientesObterTodos = "CreditosClientesObterTodos",
  DescontoAdicionar = "DescontoAdicionar",
  DescontoAtualizar = "DescontoAtualizar",
  DescontoObterPorId = "DescontoObterPorId",
  DescontoObterTodos = "DescontoObterTodos",
  DescontoRemover = "DescontoRemover",
  DocumentoAdicionar = "DocumentoAdicionar",
  DocumentoAtualizar = "DocumentoAtualizar",
  DocumentoObterPorId = "DocumentoObterPorId",
  DocumentoObterTodos = "DocumentoObterTodos",
  DocumentoRemover = "DocumentoRemover",
  EncerrarAssinatura = "EncerrarAssinatura",
  ExercicioAdicionar = "ExercicioAdicionar",
  ExercicioAtualizar = "ExercicioAtualizar",
  ExercicioObterPorId = "ExercicioObterPorId",
  ExercicioObterTodos = "ExercicioObterTodos",
  ExercicioRemover = "ExercicioRemover",
  FaturaAtualizar = "FaturaAtualizar",
  FaturamentoPorPessoaId = "FaturamentoPorPessoaId",
  FaturaObterTodos = "FaturaObterTodos",
  FaturaRemover = "FaturaRemover",
  FichaAdicionar = "FichaAdicionar",
  FichaAtualizar = "FichaAtualizar",
  FichaObterPorId = "FichaObterPorId",
  FichaObterTodos = "FichaObterTodos",
  FichaRemover = "FichaRemover",
  FornecedorAdicionar = "FornecedorAdicionar",
  FornecedorAtualizar = "FornecedorAtualizar",
  FornecedorObterPorId = "FornecedorObterPorId",
  FornecedorObterTodos = "FornecedorObterTodos",
  FornecedorRemover = "FornecedorRemover",
  FuncoesAdicionar = "FuncoesAdicionar",
  FuncoesAtualizar = "FuncoesAtualizar",
  FuncoesObterPorId = "FuncoesObterPorId",
  FuncoesObterTodos = "FuncoesObterTodos",
  FuncoesRemover = "FuncoesRemover",
  FunisAdicionar = "FunisAdicionar",
  FunisAdicionarGatilho = "FunisAdicionarGatilho",
  FunisAtualizar = "FunisAtualizar",
  FunisObterPorId = "FunisObterPorId",
  FunisObterTodos = "FunisObterTodos",
  FunisRemover = "FunisRemover",
  GetChatInfo = "GetChatInfo",
  LiberacaoAssinaturaPessoaAdicionar = "LiberacaoAssinaturaPessoaAdicionar",
  LiberacaoAssinaturaPessoaRemover = "LiberacaoAssinaturaPessoaRemover",
  ListarTodasApuracoes = "ListarTodasApuracoes",
  ListChats = "ListChats",
  MultiploAgendamento = "MultiploAgendamento",
  NovoEncerramentoAssinaturaPessoa = "NovoEncerramentoAssinaturaPessoa",
  ObterAssinaturaPessoaEncerramentoPorId = "ObterAssinaturaPessoaEncerramentoPorId",
  ObterAssinaturaPessoaRecessoPorId = "ObterAssinaturaPessoaRecessoPorId",
  ObterChequePorId = "ObterChequePorId",
  ObterPedidoVenda = "ObterPedidoVenda",
  PagamentoAdicionar = "PagamentoAdicionar",
  PagamentoAtualizar = "PagamentoAtualizar",
  PagamentoEstornar = "PagamentoEstornar",
  PagamentoObterPorId = "PagamentoObterPorId",
  PagamentoObterTodos = "PagamentoObterTodos",
  PagamentoRemover = "PagamentoRemover",
  PostagemAdicionar = "PostagemAdicionar",
  PostagemAtualizar = "PostagemAtualizar",
  PostagemObterPorId = "PostagemObterPorId",
  PostagemObterTodos = "PostagemObterTodos",
  PostagemRemover = "PostagemRemover",
  ProdutoAdicionar = "ProdutoAdicionar",
  ProdutoAtualizar = "ProdutoAtualizar",
  ProdutoObterPorId = "ProdutoObterPorId",
  ProdutoObterTodos = "ProdutoObterTodos",
  ProdutoRemover = "ProdutoRemover",
  RecebimentoAtualizarJuros = "RecebimentoAtualizarJuros",
  RecebimentoAtualizarMulta = "RecebimentoAtualizarMulta",
  RecebimentoDescontoCadastrado = "RecebimentoDescontoCadastrado",
  RecebimentoDescontoManual = "RecebimentoDescontoManual",
  RecebimentoEstornar = "RecebimentoEstornar",
  RecebimentoObterTodos = "RecebimentoObterTodos",
  RemessaObterTodos = "RemessaObterTodos",
  RemoverAssinaturaPessoaRecesso = "RemoverAssinaturaPessoaRecesso",
  RetornoAdicionar = "RetornoAdicionar",
  RetornoObterTodos = "RetornoObterTodos",
  ServicoAdicionar = "ServicoAdicionar",
  ServicoAtualizar = "ServicoAtualizar",
  ServicoObterPorId = "ServicoObterPorId",
  ServicoObterTodos = "ServicoObterTodos",
  ServicoPessoaAdicionar = "ServicoPessoaAdicionar",
  ServicoPessoaAtualizar = "ServicoPessoaAtualizar",
  ServicoPessoaObterPorId = "ServicoPessoaObterPorId",
  ServicoPessoaObterTodos = "ServicoPessoaObterTodos",
  ServicoPessoaRemover = "ServicoPessoaRemover",
  ServicoRemover = "ServicoRemover",
  TipoContaPagarAdicionar = "TipoContaPagarAdicionar",
  TipoContaPagarAtualizar = "TipoContaPagarAtualizar",
  TipoContaPagarObterPorId = "TipoContaPagarObterPorId",
  TipoContaPagarRemover = "TipoContaPagarRemover",
  TiposContaPagarObterTodos = "TiposContaPagarObterTodos",
  TreinoPreDefinidoAdicionar = "TreinoPreDefinidoAdicionar",
  TreinoPreDefinidoAtualizar = "TreinoPreDefinidoAtualizar",
  TreinoPreDefinidoObterPorId = "TreinoPreDefinidoObterPorId",
  TreinoPreDefinidoObterTodos = "TreinoPreDefinidoObterTodos",
  TreinoPreDefinidoRemover = "TreinoPreDefinidoRemover",
  UsuarioAdicionar = "UsuarioAdicionar",
  UsuarioAtualizar = "UsuarioAtualizar",
  UsuarioObterPorId = "UsuarioObterPorId",
  UsuarioObterTodos = "UsuarioObterTodos",
  UsuarioRemover = "UsuarioRemover",
  VendaAdicionar = "VendaAdicionar",
  VendaAdicionarDesconto = "VendaAdicionarDesconto",
  VendaAtualizar = "VendaAtualizar",
  VendaObterPedido = "VendaObterPedido",
  VendaObterPorId = "VendaObterPorId",
  VendaObterTodos = "VendaObterTodos",
  VendaRemover = "VendaRemover",
  VisaoGeralAdministrador = "VisaoGeralAdministrador",
  VisaoGeralImplatacao = "VisaoGeralImplatacao",
  VisaoGeralSuporte = "VisaoGeralSuporte",
  VisaoGeralVendas = "VisaoGeralVendas",
  VisitanteAdicionar = "VisitanteAdicionar",
  VisitanteAtualizar = "VisitanteAtualizar",
  VisitanteObterPorId = "VisitanteObterPorId",
  VisitanteObterTodos = "VisitanteObterTodos",
  VisitanteRemover = "VisitanteRemover",
}

export const SelfUserStore = signalStore(
  { providedIn: 'root' },
  withState(initialSelfUserStore),
  withComputed((state, systemModulesStore = inject(SystemModulesStore)) => ({
    licensedModuleNames: computed(() => systemModulesStore.moduleNames())
  })),
  withMethods((
    state,
    injector = inject(Injector),
    authService = inject(AuthService),
    authStore = inject(AuthStore),
    alertStore = inject(AlertStore),
    errorStore = inject(ErrorStore),
    crypt = inject(Encrypt),
    temporaryAuthModalService = inject(TemporaryAuthModalService)
  ) => {
    const getUserService = (): UserService => injector.get(UserService);
    const getAccountService = (): AccountService => injector.get(AccountService);
    const getPersonService = (): PersonService => injector.get(PersonService);
    let temporaryPermissionTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearTemporaryPermissionState = () => {
      if (temporaryPermissionTimeout) {
        clearTimeout(temporaryPermissionTimeout);
        temporaryPermissionTimeout = null;
      }

      patchState(state, {
        impersonatedUserProfile: null,
        temporaryPermissionsExpiresAt: null,
      });
    };

    return {
      clearSelfUserStore() {
        clearTemporaryPermissionState();
        patchState(state, {
          ...initialSelfUserStore,
          ...setPropInit(
            'loadSelfUser',
            'loadSelfUserProfile',
            'loadSelfUserRole',
            'changeMyPassword',
            'getPersonDataAsync'
          ),
        });
      },

      /**
       * Sets userSettings and parsed uiClientSettings from API response (e.g. getShortcuts).
       */
      setUserSettingsFromResponse(data: IUserSettings | null): void {
        patchState(state, {
          userSettings: data,
          uiClientSettings: parseUiClientSettings(data?.UiClientSettings),
          uiClientSettingsDirty: false,
        });
      },

      /**
       * Updates the local uiClientSettings state with a partial update.
       * Does not call the API - use persistUiClientSettings for persistence.
       */
      patchUiClientSettings(partial: Partial<IUiClientSettings>): void {
        const currentSettings = state.uiClientSettings();
        const merged = mergeUiClientSettings(currentSettings, partial);
        patchState(state, {
          uiClientSettings: merged,
          uiClientSettingsDirty: true,
        });
      },

      /**
       * Triggers persistence of the current uiClientSettings to the API.
       * Debounced (100ms) to avoid flooding the API.
       */
      persistUiClientSettings(): void {
        uiClientSettingsPersistenceSubject.next();
      },

      /**
       * Centralized logic: whether to apply server ElasticSearch settings and the merged values.
       * Used by ElasticSearchComponent to sync uiClientSettings.ElasticSearch into local UI state.
       * Returns null when nothing should be applied (no stored data, within grace period, or no changes).
       */
      getElasticSearchStateToApply(
        currentSearchOptions: Array<{ type: string; enabled: boolean; tabActive: boolean }>,
        currentPersonActions: Array<{ id: string; enabled: boolean }>,
        lastLocalChangeTimestamp: number,
        hasServerSettingsApplied: boolean,
        defaultSearchOptions: Array<{ type: string; enabled: boolean; tabActive: boolean }>,
        defaultPersonActions: Array<{ id: string; enabled: boolean }>
      ): { searchOptions: typeof currentSearchOptions; personActions: typeof currentPersonActions } | null {
        const uiClientSettings = state.uiClientSettings();
        const elastic = uiClientSettings?.ElasticSearch;
        if (!elastic) {
          return null;
        }
        const { SearchOptions: storedSearchOptions, PersonActions: storedPersonActions } = elastic;
        if (
          (!storedSearchOptions || storedSearchOptions.length === 0) &&
          (!storedPersonActions || storedPersonActions.length === 0)
        ) {
          return null;
        }
        const timeSinceLastLocalChange = Date.now() - lastLocalChangeTimestamp;
        if (
          hasServerSettingsApplied &&
          timeSinceLastLocalChange < ELASTIC_SEARCH_LOCAL_CHANGE_GRACE_PERIOD_MS
        ) {
          return null;
        }
        let searchOptionsResult: typeof currentSearchOptions | null = null;
        let personActionsResult: typeof currentPersonActions | null = null;
        if (storedSearchOptions && storedSearchOptions.length > 0) {
          const merged = storageFormatToSearchOptions(storedSearchOptions, defaultSearchOptions);
          const hasChanges = merged.some(
            (opt, idx) =>
              opt.enabled !== currentSearchOptions[idx]?.enabled ||
              opt.tabActive !== currentSearchOptions[idx]?.tabActive
          );
          if (hasChanges) {
            searchOptionsResult = merged as typeof currentSearchOptions;
          }
        }
        if (storedPersonActions && storedPersonActions.length > 0) {
          const merged = storageFormatToPersonActions(storedPersonActions, defaultPersonActions);
          const hasChanges = merged.some(
            (action, idx) => action.enabled !== currentPersonActions[idx]?.enabled
          );
          if (hasChanges) {
            personActionsResult = merged as typeof currentPersonActions;
          }
        }
        if (searchOptionsResult === null && personActionsResult === null) {
          return null;
        }
        return {
          searchOptions: searchOptionsResult ?? (currentSearchOptions as typeof currentSearchOptions),
          personActions: personActionsResult ?? (currentPersonActions as typeof currentPersonActions),
        };
      },

      isAdminScope: (): boolean => {
        const tenantId = JSON.parse(localStorage.getItem('actuar/authorization'))?.TenantId;;
        const isAdmin = tenantId === adminTenants?.[1].account;
        const isPaguelaAdmin = tenantId === adminTenants?.[5].account;
        const isFacilFitAdmin = tenantId === adminTenants?.[8].account;
        const isToletusAdmin = tenantId === adminTenants?.[9].account;
        const isAdminScope = isAdmin || isPaguelaAdmin || isFacilFitAdmin || isToletusAdmin;
        return isAdminScope;
      },

      isPaguelaAdmin: (): boolean => {
       const tenantId = JSON.parse(localStorage.getItem('actuar/authorization'))?.TenantId;
        const isPaguelaAdmin = tenantId === adminTenants.find(t => t?.name === 'Paguela')?.account;
        return isPaguelaAdmin;
      },

      isFacilFitAdmin: (): boolean => {
        const tenantId = JSON.parse(localStorage.getItem('actuar/authorization'))?.TenantId;
        const isFacilFitAdmin = tenantId === adminTenants.find(t => t?.name === 'Facilfit')?.account;
        return isFacilFitAdmin;
      },

      isFacilFitOrActuarAdmin: (): boolean => {
        const tenantId = JSON.parse(localStorage.getItem('actuar/authorization'))?.TenantId;
        const isFacilFitAdmin = tenantId === adminTenants.find(t => t?.name === 'Facilfit')?.account;
        const isActuarAdmin = tenantId === adminTenants?.[1].account; // Actuar admin
        return isFacilFitAdmin || isActuarAdmin;
      },

      isThiago: (): boolean => {
        const personId = JSON.parse(localStorage.getItem('actuar/authorization'))?.PersonId;
        return personId === '0201cf20-a8fc-43e8-a792-8686e52946fd';
      },

      isTestAccount: (): boolean => {
        const tenantId = JSON.parse(localStorage.getItem('actuar/authorization'))?.TenantId;
        const isTestAccount = tenantId === adminTenants?.[0].account;
        return isTestAccount;
      },

      getSelfPersonId: (): string => {
        const personId = JSON.parse(localStorage.getItem('actuar/authorization'))?.PersonId;
        return personId;
      },

      getAuthorization: (): IAuthAccessToken => {
        const authorization = localStorage.getItem('actuar/authorization');
        return authorization ? JSON.parse(authorization) : null;
      },

      hasClientPermission(permission: number): boolean {
        const profile = state.userProfile();

        if (profile?.IsOwner) {
          return true;
        }

        const clientPermissions = profile?.ClientPermissions;

        if (!clientPermissions?.length) {
          return false;
        }

        return clientPermissions.some(
          ({ Permission, Checked }) => Checked && Permission === permission
        );
      },

      hasClientPermissionGroup(permissionGroup: string | string[]): boolean {
        const profile = state.userProfile();

        if (profile?.IsOwner) {
          return true;
        }

        const clientPermissions = profile?.ClientPermissions;

        if (!clientPermissions?.length) {
          return false;
        }

        const groups = Array.isArray(permissionGroup)
          ? permissionGroup
          : [permissionGroup];

        return clientPermissions.some(
          ({ Group, Checked }) => Checked && groups.includes(Group)
        );
      },

      hasAllClientPermissions(permissions: number[]): boolean {
        if (!Array.isArray(permissions) || permissions.length === 0) {
          return false;
        }

        const profile = state.userProfile();

        if (profile?.IsOwner) {
          return true;
        }

        const clientPermissions = profile?.ClientPermissions;

        if (!clientPermissions?.length) {
          return false;
        }

        const allowedPermissions = new Set(
          clientPermissions
            .filter(({ Checked }) => Checked)
            .map(({ Permission }) => Permission)
        );

        return permissions.every((permission) => allowedPermissions.has(permission));
      },

      ensurePermissions: async (
        permissions: number[],
        config?: Omit<TemporaryAuthModalConfig, 'requiredPermissions'> & { interactive?: boolean },
      ): Promise<boolean> => {
        if (!Array.isArray(permissions) || permissions.length === 0) {
          return true;
        }
        const interactive = config?.interactive ?? true;

        const hasPermissionsInProfile = (profile: UserProfile): boolean => {
          if (!profile) {
            return false;
          }

          if (profile.IsOwner) {
            return true;
          }

          const clientPermissions = profile.ClientPermissions;

          if (!clientPermissions?.length) {
            return false;
          }

          const allowed = new Set(
            clientPermissions
              .filter(({ Checked }) => Checked)
              .map(({ Permission }) => Permission),
          );

          return permissions.every((permission) => allowed.has(permission));
        };

        let currentProfile = state.userProfile();
        if (!currentProfile) {
          try {
            currentProfile = await firstValueFrom(getUserService().getSelfUserProfile());
            if (currentProfile) {
              patchState(state, { userProfile: currentProfile });
            }
          } catch {
          }
        }

        if (hasPermissionsInProfile(currentProfile)) {
          return true;
        }
        const basicUserData = state.userData();
        if (basicUserData?.IsOwner) {
          return true;
        }

        const activeImpersonation = state.impersonatedUserProfile();
        const expiration = state.temporaryPermissionsExpiresAt();

        if (expiration && expiration <= Date.now()) {
          authStore.endTemporaryAuthSession();
          clearTemporaryPermissionState();
        } else if (activeImpersonation && expiration && expiration > Date.now()) {
          if (hasPermissionsInProfile(activeImpersonation)) {
            return true;
          }
        }

        if (hasPermissionsInProfile(state.userProfile())) {
          return true;
        }

        clearTemporaryPermissionState();

        if (!interactive) {
          return false;
        }

        const modalResult = await firstValueFrom(
          temporaryAuthModalService.open({
            requiredPermissions: permissions,
            title: config?.title,
            message: config?.message,
            durationMs: config?.durationMs,
            authProvider: (payload) => authService.login(payload),
          }),
        ).catch(() => null);

        if (!modalResult || modalResult.status !== 'success' || !modalResult.authData) {
          return false;
        }

        const duration = modalResult.durationMs ?? 30_000;

        try {
          authStore.beginTemporaryAuthSession(modalResult.authData, duration);

          const impersonatedProfile = await firstValueFrom(getUserService().getSelfUserProfile());

          if (!hasPermissionsInProfile(impersonatedProfile)) {
            authStore.endTemporaryAuthSession();
            alertStore.create({
              type: 'warning',
              title: `${TEMPORARY_AUTH_TRANSLATION_PATH}.ALERT.INSUFFICIENT_PERMISSIONS_TITLE`,
              message: `${TEMPORARY_AUTH_TRANSLATION_PATH}.ALERT.INSUFFICIENT_PERMISSIONS_MESSAGE`,
            });
            return false;
          }

          patchState(state, {
            impersonatedUserProfile: impersonatedProfile,
            temporaryPermissionsExpiresAt: Date.now() + duration,
          });

          temporaryPermissionTimeout = setTimeout(() => {
            clearTemporaryPermissionState();
            authStore.endTemporaryAuthSession();
          }, duration);

          return true;
        } catch (error) {
          authStore.endTemporaryAuthSession();
          clearTemporaryPermissionState();
          errorStore.setHttpError(error);
          return false;
        }
      },

      loadSelfUser: rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('loadSelfUser') })),
          switchMap(() => getUserService().getSelfUser().pipe(
            tapResponse({
              next: (data: IUser) => {
                const activeAccount = localStorage.getItem('activeAccount');

                if (activeAccount) {
                  const activeAccountParsed = JSON.parse(activeAccount);
                  data.backgroundColor = activeAccountParsed?.backgroundColor;
                } else {
                  data.backgroundColor = 'bg-purple';
                }

                patchState(state, { userData: data, ...setPropLoaded('loadSelfUser') });
              },
              error: (error) => {
                patchState(state, { ...setPropError('loadSelfUser', error) })
              }
            }
            )
          ))
        )
      ),
      loadSelfUserProfile: rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('loadSelfUserProfile') })),
          switchMap(() => getUserService().getSelfUserProfile().pipe(
            tapResponse({
              next: (data: UserProfile) => {
                patchState(state, { userProfile: data, ...setPropLoaded('loadSelfUserProfile') });
              },
              error: (error) => {
                patchState(state, { ...setPropError('loadSelfUserProfile', error) });
              }
            })
          ))
        )
      ),
      loadSelfUserRole: rxMethod(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('loadSelfUserRole') })),
          switchMap(() => getUserService().getSelfUserRole().pipe(
            tapResponse({
              next: (data: IUserRole) => {
                patchState(state, { userRole: data, ...setPropLoaded('loadSelfUserRole') });
              },
              error: (error) => {
                patchState(state, { ...setPropError('loadSelfUserRole', error) })
              }
            }
            )
          ))
        )
      ),
      changeMyPassword: rxMethod<IChangePaswordRequest>(
        pipe(
          tap((payload: IChangePaswordRequest) => {
            patchState(state, { ...setPropLoading('changeMyPassword') })
            payload.OldPassword = crypt.encryptMd5(
              payload.Email + payload.OldPassword
            );
            payload.NewPassword = crypt.encryptMd5(
              payload.Email + payload.NewPassword
            );
            return payload;
          }),
          switchMap((payload) => getAccountService().changeSelfUserPassword(payload).pipe(
            tapResponse({
              next: () => {
                patchState(state, { ...setPropLoaded('changeMyPassword') });
                alertStore.create({
                  title: 'ALERT.TITLE_SUCCESS',
                  type: 'success',
                  message: 'PROFILEMENU.ACCOUNT.ALERT.CHANGE_PASSWORD.MESSAGE',

                });
              },
              error: (error: any) => {
                patchState(state, { ...setPropError('changeMyPassword', error) })
                errorStore.setHttpError(error);
              }
            }
            )
          ))
        )
      ),
      getAllPersonByUser: rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('getAllPersonsByUser') })),
          switchMap(() => getUserService().getAllPersonsByUser().pipe(
            tapResponse({
              next: (data) => {
                data.forEach((item, index) => {
                  const userData = state.userData()
                  item.backgroundColor = `${getCyclicColor(index)}`;
                  if (item.PersonId === state.userData().PersonId) {
                    item.backgroundColor = userData.backgroundColor;
                  }
                });
                patchState(state, { allPersonsByUser: data, ...setPropLoaded('getAllPersonsByUser') });
              },
              error: (error) => {
                patchState(state, { ...setPropError('getAllPersonsByUser', error) })
              }
            }
            ))
          )
        )
      ),

      getPersonDataAsync: rxMethod<void>(pipe(
        tap(() => patchState(state, { ...setPropLoading('getPersonDataAsync') })),
        switchMap(() => getPersonService().getUserDataAsync().pipe(
            tapResponse({
                next: (personDataAsync) => {
                    patchState(state, { personDataAsync, ...setPropLoaded('getPersonDataAsync') })
                },
                error: (error) => {
                    patchState(state, { ...setPropError('getPersonDataAsync', error )})
                },
            })
        ))
    ))
    }
  }),
  withReqState(
    'loadSelfUser',
    'loadSelfUserProfile',
    'loadSelfUserRole',
    'changeMyPassword',
    'getAllPersonsByUser',
    'getPersonDataAsync'
  )
)
